import LayoutHR from "layouts/layout-hr";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { getRequest, patchRequest, postRequest } from "services/api";
import Swal from "sweetalert2";
import {
  HR_STAFF_LOAN_MANAGE_PERMISSIONS,
  HR_STAFF_LOAN_SETTINGS_PERMISSIONS,
} from "utils/hrPermissionKeys";
import { buildQueryString } from "utils/queryHelpers";
import "./staff-loan-page.css";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(amount || 0));

const formatNumberInput = (amount) =>
  amount === "" || amount === null || amount === undefined
    ? ""
    : new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(Number(String(amount).replace(/\D/g, "") || 0));

const normalizeCurrencyInput = (value) => String(value || "").replace(/\D/g, "");

const formatRatio = (ratio) => {
  const value = Number(ratio || 0);
  if (!value) return "0%";
  return `${Math.round(value * 100)}%`;
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const statusLabels = {
  draft: "Draft",
  pending: "Menunggu Approval",
  approved: "Disetujui",
  active: "Aktif",
  paused: "Dijeda",
  paid: "Lunas",
  rejected: "Ditolak",
  cancelled: "Dibatalkan",
};

const statusClass = {
  draft: "bg-secondary-lt text-secondary",
  pending: "bg-warning-lt text-warning",
  approved: "bg-blue-lt text-blue",
  active: "bg-success-lt text-success",
  paused: "bg-purple-lt text-purple",
  paid: "bg-teal-lt text-teal",
  rejected: "bg-red-lt text-red",
  cancelled: "bg-secondary-lt text-secondary",
  unpaid: "bg-warning-lt text-warning",
  skipped: "bg-secondary-lt text-secondary",
};

const emptyLoanForm = {
  user_id: "",
  amount: "",
  tenor_months: 1,
  purpose: "",
  reason: "",
  note: "",
  start_period: "",
};

const defaultSettingsForm = {
  monthly_max_amount: "",
  monthly_max_request_count: 1,
  min_installment_amount: "",
  max_tenor_months: 12,
  max_salary_ratio_percent: 30,
  salary_base_type: "total_salary",
  approval_flow_enabled: true,
  allow_multiple_active_loans: false,
  allow_pause_installment: false,
  default_start_period_rule: "next_payroll_period",
};

const getDepartmentName = (loan) => loan?.user?.department?.name || loan?.department?.name || "Tanpa Departemen";

const getRepaymentProgress = (loan) => {
  const repayments = loan?.repayments || [];
  if (!repayments.length) return { paid: 0, total: 0, percent: 0, estimated: "-" };
  const paid = repayments.filter((repayment) => repayment.status === "paid").length;
  const estimated = repayments.slice().reverse().find((repayment) => repayment.status !== "paid")?.period || "Lunas";
  return { paid, total: repayments.length, percent: Math.round((paid / repayments.length) * 100), estimated };
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const getSelectValue = (options, value) => options.find((option) => String(option.value) === String(value)) || null;

const StaffLoanPage = () => {
  const [activeTab, setActiveTab] = useState("loans");
  const [loans, setLoans] = useState({ data: [], pagination: null, loading: false, error: null });
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(defaultSettingsForm);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    department_id: "",
    status: "",
    has_outstanding: "",
    start_period: "",
    end_period: "",
    page: 1,
    limit: 10,
  });
  const [loanForm, setLoanForm] = useState(emptyLoanForm);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [loanDetail, setLoanDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const fetchSettings = useCallback(async () => {
    const response = await getRequest("/ahr/staff-loan-settings");
    if (response?.success) {
      setSettings(response.data);
      setSettingsForm({
        monthly_max_amount: response.data.monthly_max_amount || "",
        monthly_max_request_count: response.data.monthly_max_request_count || 1,
        min_installment_amount: response.data.min_installment_amount || "",
        max_tenor_months: response.data.max_tenor_months || 12,
        max_salary_ratio_percent: Math.round(Number(response.data.max_salary_ratio || 0) * 100),
        salary_base_type: response.data.salary_base_type || "total_salary",
        approval_flow_enabled: Boolean(response.data.approval_flow_enabled),
        allow_multiple_active_loans: Boolean(response.data.allow_multiple_active_loans),
        allow_pause_installment: Boolean(response.data.allow_pause_installment),
        default_start_period_rule: response.data.default_start_period_rule || "next_payroll_period",
      });
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    const response = await getRequest("/user?all=1&include=department,position,roles&orderBy=sort&order=asc");
    if (response?.success) setUsers(response.data || []);
  }, []);

  const fetchDepartments = useCallback(async () => {
    const response = await getRequest("/ahr/departments?all=1");
    if (response?.success) setDepartments(response.data || []);
  }, []);

  const fetchLoans = useCallback(async () => {
    setLoans((current) => ({ ...current, loading: true, error: null }));
    const queryString = buildQueryString({
      search: filters.search || "",
      page: filters.page,
      limit: filters.limit,
      "filter[department_id]": filters.department_id || "",
      "filter[status]": filters.status || "",
      "filter[has_outstanding]": filters.has_outstanding || "",
      "filter[start_period]": filters.start_period || "",
      "filter[end_period]": filters.end_period || "",
      orderBy: "created_at",
      sort: "desc",
    });

    try {
      const response = await getRequest(`/ahr/staff-loans${queryString}`);
      if (response?.success) {
        setLoans({ data: response.data || [], pagination: response.pagination || null, loading: false, error: null });
      }
    } catch (error) {
      setLoans({ data: [], pagination: null, loading: false, error: error.message });
    }
  }, [filters]);

  const fetchLoanDetail = useCallback(async (loanId) => {
    if (!loanId) return;
    setDetailLoading(true);
    try {
      const response = await getRequest(`/ahr/staff-loans/${loanId}`);
      if (response?.success) setLoanDetail(response.data);
    } catch (error) {
      Swal.fire("Gagal", error.message || "Gagal mengambil detail kasbon.", "error");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchUsers();
    fetchDepartments();
  }, [fetchDepartments, fetchSettings, fetchUsers]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  useEffect(() => {
    fetchLoanDetail(selectedLoanId);
  }, [fetchLoanDetail, selectedLoanId]);

  useEffect(() => {
    const userId = loanForm.user_id;
    const amount = Number(loanForm.amount);
    const tenor = Number(loanForm.tenor_months);
    if (!userId || amount <= 0 || tenor <= 0) {
      setEligibility(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setEligibilityLoading(true);
      try {
        const response = await postRequest("/ahr/staff-loans/eligibility-check", {
          user_id: userId,
          amount,
          tenor_months: tenor,
        });
        if (response?.success) setEligibility(response.data);
      } catch (error) {
        setEligibility({ eligible: false, reasons: [error.message] });
      } finally {
        setEligibilityLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [loanForm.amount, loanForm.tenor_months, loanForm.user_id]);

  const groupedUsers = useMemo(() => {
    return users.reduce((groups, user) => {
      const departmentName = user.department?.name || "Tanpa Departemen";
      if (!groups[departmentName]) groups[departmentName] = [];
      groups[departmentName].push(user);
      return groups;
    }, {});
  }, [users]);

  const departmentOptions = useMemo(() => [
    { value: "", label: "Semua" },
    ...departments.map((department) => ({ value: department.id, label: department.name })),
  ], [departments]);

  const statusOptions = useMemo(() => [
    { value: "", label: "Semua" },
    ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
  ], []);

  const userOptions = useMemo(() => {
    return Object.entries(groupedUsers).flatMap(([departmentName, departmentUsers]) =>
      departmentUsers.map((user) => ({
        value: user.id,
        label: user.name,
        department: departmentName,
        email: user.email,
      }))
    );
  }, [groupedUsers]);

  const salaryBaseOptions = [
    { value: "total_salary", label: "Total gaji" },
    { value: "basic_salary", label: "Gaji pokok" },
    { value: "take_home", label: "Take home" },
  ];

  const startPeriodOptions = [
    { value: "same_month", label: "Bulan ini" },
    { value: "next_month", label: "Bulan depan" },
    { value: "next_payroll_period", label: "Periode payroll berikutnya" },
  ];

  const yesNoOptions = [
    { value: "1", label: "Ya" },
    { value: "0", label: "Tidak" },
  ];

  const groupedLoans = useMemo(() => {
    return loans.data.reduce((groups, loan) => {
      const departmentName = getDepartmentName(loan);
      if (!groups[departmentName]) groups[departmentName] = [];
      groups[departmentName].push(loan);
      return groups;
    }, {});
  }, [loans.data]);

  const selectedUser = users.find((user) => user.id === loanForm.user_id);
  const totalPages = loans.pagination?.last_page || 1;
  const currentPage = loans.pagination?.current_page || filters.page;

  const updateFilters = (nextFilters) => {
    setFilters((current) => ({ ...current, ...nextFilters, page: nextFilters.page || 1 }));
  };

  const handleCreateLoan = async (event) => {
    event.preventDefault();
    if (eligibility && eligibility.eligible === false) return;
    setFormSubmitting(true);
    try {
      const payload = {
        user_id: loanForm.user_id,
        amount: Number(loanForm.amount),
        tenor_months: Number(loanForm.tenor_months),
        purpose: loanForm.purpose || undefined,
        reason: loanForm.reason || undefined,
        note: loanForm.note || undefined,
        start_period: loanForm.start_period || undefined,
      };
      const response = await postRequest("/ahr/staff-loans", payload);
      if (response?.success) {
        Swal.fire("Berhasil", response.message || "Pengajuan kasbon berhasil dibuat.", "success");
        setLoanForm(emptyLoanForm);
        setEligibility(null);
        setActiveTab("loans");
        fetchLoans();
        if (response.data?.id) setSelectedLoanId(response.data.id);
      }
    } catch (error) {
      Swal.fire("Gagal", error.message || "Gagal membuat pengajuan kasbon.", "error");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    setSettingsSaving(true);
    try {
      const response = await patchRequest("/ahr/staff-loan-settings", {
        monthly_max_amount: Number(settingsForm.monthly_max_amount || 0),
        monthly_max_request_count: Number(settingsForm.monthly_max_request_count || 1),
        min_installment_amount: Number(settingsForm.min_installment_amount || 0),
        max_tenor_months: Number(settingsForm.max_tenor_months || 1),
        max_salary_ratio: Number(settingsForm.max_salary_ratio_percent || 0) / 100,
        salary_base_type: settingsForm.salary_base_type,
        approval_flow_enabled: Boolean(settingsForm.approval_flow_enabled),
        allow_multiple_active_loans: Boolean(settingsForm.allow_multiple_active_loans),
        allow_pause_installment: Boolean(settingsForm.allow_pause_installment),
        default_start_period_rule: settingsForm.default_start_period_rule,
      });
      if (response?.success) {
        Swal.fire("Berhasil", response.message || "Setting kasbon berhasil disimpan.", "success");
        fetchSettings();
      }
    } catch (error) {
      Swal.fire("Gagal", error.message || "Gagal menyimpan setting kasbon.", "error");
    } finally {
      setSettingsSaving(false);
    }
  };

  const openActionDialog = (action, loan) => {
    setActionDialog({
      action,
      loan,
      note: "",
      disbursement_date: new Date().toISOString().slice(0, 10),
      start_period: loan.start_period || "",
    });
  };

  const handleSubmitAction = async (event) => {
    event.preventDefault();
    if (!actionDialog) return;
    if (actionDialog.action === "reject" && !actionDialog.note.trim()) {
      Swal.fire("Catatan wajib", "Alasan penolakan wajib diisi.", "warning");
      return;
    }

    if (actionDialog.action === "disburse") {
      const confirmation = await Swal.fire({
        title: "Cairkan kasbon?",
        text: "Pencairan akan membuat jadwal cicilan dan status kasbon menjadi aktif.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, cairkan",
        cancelButtonText: "Batal",
      });
      if (!confirmation.isConfirmed) return;
    }

    const payload = {};
    if (actionDialog.note) payload.note = actionDialog.note;
    if (actionDialog.action === "disburse") {
      if (actionDialog.disbursement_date) payload.disbursement_date = actionDialog.disbursement_date;
      if (actionDialog.start_period) payload.start_period = actionDialog.start_period;
    }

    setActionSubmitting(true);
    try {
      const response = await postRequest(`/ahr/staff-loans/${actionDialog.loan.id}/${actionDialog.action}`, payload);
      if (response?.success) {
        Swal.fire("Berhasil", response.message || "Status kasbon berhasil diperbarui.", "success");
        setActionDialog(null);
        setLoanDetail(response.data || null);
        fetchLoans();
      }
    } catch (error) {
      Swal.fire("Gagal", error.message || "Gagal memproses kasbon.", "error");
    } finally {
      setActionSubmitting(false);
    }
  };

  const renderActionButtons = (loan) => {
    const canApprove = loan.status === "pending";
    const canReject = loan.status === "pending";
    const canDisburse = loan.status === "approved" && !(loan.repayments || []).length;
    const canPause = loan.status === "active" && settings?.allow_pause_installment;
    const canResume = loan.status === "paused";
    const canCancel = ["draft", "pending", "approved"].includes(loan.status);

    return (
      <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_MANAGE_PERMISSIONS}>
        <div className="staff-loan-action-group">
          {canApprove && <button className="btn btn-sm btn-success" onClick={() => openActionDialog("approve", loan)}>Setujui</button>}
          {canReject && <button className="btn btn-sm btn-danger" onClick={() => openActionDialog("reject", loan)}>Tolak</button>}
          {canDisburse && <button className="btn btn-sm btn-primary" onClick={() => openActionDialog("disburse", loan)}>Cairkan</button>}
          {canPause && <button className="btn btn-sm btn-warning" onClick={() => openActionDialog("pause", loan)}>Jeda</button>}
          {canResume && <button className="btn btn-sm btn-success" onClick={() => openActionDialog("resume", loan)}>Lanjutkan</button>}
          {canCancel && <button className="btn btn-sm btn-outline-danger" onClick={() => openActionDialog("cancel", loan)}>Batalkan</button>}
          {!canApprove && !canReject && !canDisburse && !canPause && !canResume && !canCancel && (
            <span className="text-muted small">Tidak ada aksi</span>
          )}
        </div>
      </PermissionMiddleware>
    );
  };

  return (
    <LayoutHR>
      <div className="staff-loan-page">
        <div className="page-header d-print-none mb-3">
          <div className="row align-items-center g-2">
            <div className="col">
              <h2 className="page-title">Kasbon Staff</h2>
              <div className="text-muted mt-1">Catat pengajuan kasbon, setujui, cairkan, dan pantau sisa cicilannya.</div>
            </div>
            <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_MANAGE_PERMISSIONS}>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => setActiveTab("request")}>Ajukan Kasbon</button>
              </div>
            </PermissionMiddleware>
          </div>
        </div>

        <div className="staff-loan-tabs mb-3">
          <button className={`staff-loan-tab ${activeTab === "loans" ? "active" : ""}`} onClick={() => setActiveTab("loans")}>
            Daftar Kasbon
          </button>
          <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_MANAGE_PERMISSIONS}>
            <button className={`staff-loan-tab ${activeTab === "request" ? "active" : ""}`} onClick={() => setActiveTab("request")}>
              Ajukan Kasbon
            </button>
          </PermissionMiddleware>
          <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_SETTINGS_PERMISSIONS}>
            <button className={`staff-loan-tab ${activeTab === "settings" ? "active" : ""}`} onClick={() => setActiveTab("settings")}>
              Atur Batas
            </button>
          </PermissionMiddleware>
        </div>

        {activeTab === "loans" && (
          <>
            <div className="card mb-3">
              <div className="card-body py-2">
                <div className="row g-2 align-items-end">
                  <div className="col-lg-5">
                    <label className="form-label small text-muted mb-1">Cari</label>
                    <input className="form-control" placeholder="Cari nama staff / nomor kasbon..." value={filters.search} onChange={(event) => updateFilters({ search: event.target.value })} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label small text-muted mb-1">Departemen</label>
                    <Select
                      classNamePrefix="react-select"
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      options={departmentOptions}
                      value={getSelectValue(departmentOptions, filters.department_id)}
                      onChange={(option) => updateFilters({ department_id: option?.value || "" })}
                    />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label small text-muted mb-1">Status</label>
                    <Select
                      classNamePrefix="react-select"
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      options={statusOptions}
                      value={getSelectValue(statusOptions, filters.status)}
                      onChange={(option) => updateFilters({ status: option?.value || "" })}
                    />
                  </div>
                  <div className="col-lg-1">
                    <button className="btn btn-outline-secondary w-100" onClick={() => setFilters({ search: "", department_id: "", status: "", has_outstanding: "", start_period: "", end_period: "", page: 1, limit: 10 })}>Reset</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4"><div className="staff-loan-summary"><span>Jumlah Kasbon</span><strong>{loans.pagination?.total || loans.data.length}</strong></div></div>
              <div className="col-md-4"><div className="staff-loan-summary"><span>Maksimal Pengajuan</span><strong>{formatCurrency(settings?.monthly_max_amount)}</strong></div></div>
              <div className="col-md-4"><div className="staff-loan-summary"><span>Batas dari Gaji</span><strong>{formatRatio(settings?.max_salary_ratio)}</strong></div></div>
            </div>

            <div className="card">
              <div className="table-responsive staff-loan-table-wrap">
                <table className="table table-vcenter table-bordered mb-0 staff-loan-excel-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Staff</th>
                      <th>No Kasbon</th>
                      <th>Nominal</th>
                      <th>Potongan / Bulan</th>
                      <th>Sisa</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.loading && <tr><td colSpan={8} className="text-center py-4">Loading data kasbon...</td></tr>}
                    {!loans.loading && loans.error && <tr><td colSpan={8} className="text-danger text-center py-4">{loans.error}</td></tr>}
                    {!loans.loading && !loans.error && loans.data.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">Data kasbon belum ada.</td></tr>}
                    {!loans.loading && Object.entries(groupedLoans).map(([departmentName, rows]) => (
                      <React.Fragment key={departmentName}>
                        <tr className="staff-loan-department-row"><td colSpan={8}>{departmentName}</td></tr>
                        {rows.map((loan, index) => {
                          const progress = getRepaymentProgress(loan);
                          return (
                            <tr key={loan.id}>
                              <td>{index + 1}</td>
                              <td className="fw-bold">
                                {loan.user?.name || "-"}
                                <div className="text-muted small">
                                  {loan.user?.employee_id || loan.user?.email || "-"}
                                  {progress.total > 0 ? ` | ${progress.paid}/${progress.total} cicilan` : ""}
                                </div>
                              </td>
                              <td className="fw-bold">{loan.loan_number || "-"}</td>
                              <td>{formatCurrency(loan.amount)}</td>
                              <td className="fw-bold text-danger">{formatCurrency(loan.installment_amount)}</td>
                              <td className="fw-bold">{formatCurrency(loan.outstanding_amount)}</td>
                              <td><span className={`badge ${statusClass[loan.status] || "bg-secondary-lt text-secondary"}`}>{statusLabels[loan.status] || loan.status}</span></td>
                              <td>
                                <div className="staff-loan-action-group">
                                  <button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedLoanId(loan.id)}>Detail</button>
                                  {renderActionButtons(loan)}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex justify-content-between align-items-center py-2">
                <span className="text-muted small">Page {currentPage} / {totalPages}</span>
                <div className="btn-list">
                  <button className="btn btn-sm btn-outline-secondary" disabled={currentPage <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Prev</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={currentPage >= totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "request" && (
          <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_MANAGE_PERMISSIONS}>
            <form onSubmit={handleCreateLoan} className="row g-3">
              <div className="col-xl-8">
                <div className="card staff-loan-form-card">
                  <div className="card-header py-2"><h3 className="card-title">Formulir Pengajuan Kasbon Baru</h3></div>
                  <div className="card-body">
                  <div className="staff-loan-form-grid staff-loan-form-grid-simple">
                    <label className="staff-loan-form-field">
                        <span>Pilih Staff</span>
                        <Select
                          classNamePrefix="react-select"
                          isSearchable
                          menuPortalTarget={document.body}
                          styles={selectStyles}
                          placeholder="Cari nama staff..."
                          options={userOptions}
                          value={getSelectValue(userOptions, loanForm.user_id)}
                          onChange={(option) => setLoanForm((current) => ({ ...current, user_id: option?.value || "" }))}
                          formatOptionLabel={(option) => (
                            <div>
                              <div className="fw-bold">{option.label}</div>
                              <div className="text-muted small">{option.department}{option.email ? ` | ${option.email}` : ""}</div>
                            </div>
                          )}
                        />
                      </label>
                      <label className="staff-loan-form-field"><span>Departemen</span><input className="form-control" value={selectedUser?.department?.name || "-"} readOnly /></label>
                      <label className="staff-loan-form-field"><span>Jumlah Kasbon</span><input inputMode="numeric" className="form-control text-end" placeholder="0" value={formatNumberInput(loanForm.amount)} onChange={(event) => setLoanForm((current) => ({ ...current, amount: normalizeCurrencyInput(event.target.value) }))} required /></label>
                      <label className="staff-loan-form-field"><span>Dicicil Berapa Bulan</span><input type="number" className="form-control text-end" min="1" max={eligibility?.max_tenor_months || settings?.max_tenor_months || undefined} value={loanForm.tenor_months} onChange={(event) => setLoanForm((current) => ({ ...current, tenor_months: event.target.value }))} required /></label>
                      <label className="staff-loan-form-field"><span>Mulai Dipotong</span><input className="form-control" placeholder="YYYY-MM, boleh kosong" value={loanForm.start_period} onChange={(event) => setLoanForm((current) => ({ ...current, start_period: event.target.value }))} /></label>
                      <label className="staff-loan-form-field"><span>Keperluan Singkat</span><input className="form-control" value={loanForm.purpose} onChange={(event) => setLoanForm((current) => ({ ...current, purpose: event.target.value }))} /></label>
                      <label className="staff-loan-form-field staff-loan-form-field-wide"><span>Alasan Pengajuan</span><textarea className="form-control" rows={3} value={loanForm.reason} onChange={(event) => setLoanForm((current) => ({ ...current, reason: event.target.value }))} /></label>
                      <label className="staff-loan-form-field staff-loan-form-field-wide"><span>Catatan Tambahan</span><textarea className="form-control" rows={2} value={loanForm.note} onChange={(event) => setLoanForm((current) => ({ ...current, note: event.target.value }))} /></label>
                    </div>
                  </div>
                  <div className="card-footer staff-loan-form-actions">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setLoanForm(emptyLoanForm)}>Reset</button>
                    <button type="submit" className="btn btn-success" disabled={formSubmitting || eligibilityLoading || eligibility?.eligible === false}>{formSubmitting ? "Menyimpan..." : "Simpan Pengajuan"}</button>
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="staff-loan-eligibility">
                <span>Hasil Cek Sistem</span>
                  {eligibilityLoading ? <strong>Memeriksa...</strong> : eligibility ? (
                    <>
                      <strong className={eligibility.eligible ? "text-success" : "text-danger"}>{eligibility.eligible ? "Layak diajukan" : "Tidak layak"}</strong>
                      <div className="staff-loan-eligibility-row"><span>Boleh sampai</span><b>{formatCurrency(eligibility.max_amount)}</b></div>
                      <div className="staff-loan-eligibility-row"><span>Maksimal bulan</span><b>{eligibility.max_tenor_months} bulan</b></div>
                      <div className="staff-loan-eligibility-row"><span>Perkiraan potongan</span><b>{formatCurrency(eligibility.suggested_installment)}</b></div>
                      {eligibility.reasons?.length > 0 && <div className="alert alert-danger mt-3 mb-0">{eligibility.reasons.map((reason) => <div key={reason}>{reason}</div>)}</div>}
                    </>
                  ) : <div className="text-muted">Pilih staff, nominal, dan tenor untuk cek kelayakan.</div>}
                  <div className="alert alert-info mb-0 mt-3">Potongan final akan mengikuti jadwal dari sistem saat kasbon dicairkan.</div>
                </div>
              </div>
            </form>
          </PermissionMiddleware>
        )}

        {activeTab === "settings" && (
          <PermissionMiddleware allowedPermissions={HR_STAFF_LOAN_SETTINGS_PERMISSIONS}>
            <form onSubmit={handleSaveSettings} className="card staff-loan-form-card">
              <div className="card-header py-2"><h3 className="card-title">Atur Batas Kasbon</h3></div>
              <div className="card-body">
                <div className="staff-loan-form-grid">
                  <label className="staff-loan-form-field"><span>Maksimal kasbon</span><input inputMode="numeric" className="form-control text-end" value={formatNumberInput(settingsForm.monthly_max_amount)} onChange={(event) => setSettingsForm((current) => ({ ...current, monthly_max_amount: normalizeCurrencyInput(event.target.value) }))} /></label>
                  <label className="staff-loan-form-field"><span>Berapa kali / bulan</span><input type="number" className="form-control text-end" value={settingsForm.monthly_max_request_count} onChange={(event) => setSettingsForm((current) => ({ ...current, monthly_max_request_count: event.target.value }))} /></label>
                  <label className="staff-loan-form-field"><span>Minimal cicilan</span><input inputMode="numeric" className="form-control text-end" value={formatNumberInput(settingsForm.min_installment_amount)} onChange={(event) => setSettingsForm((current) => ({ ...current, min_installment_amount: normalizeCurrencyInput(event.target.value) }))} /></label>
                  <label className="staff-loan-form-field"><span>Maksimal bulan cicilan</span><input type="number" className="form-control text-end" value={settingsForm.max_tenor_months} onChange={(event) => setSettingsForm((current) => ({ ...current, max_tenor_months: event.target.value }))} /></label>
                  <label className="staff-loan-form-field"><span>Batas dari gaji (%)</span><input type="number" className="form-control text-end" value={settingsForm.max_salary_ratio_percent} onChange={(event) => setSettingsForm((current) => ({ ...current, max_salary_ratio_percent: event.target.value }))} /></label>
                  <label className="staff-loan-form-field"><span>Dasar gaji</span><Select classNamePrefix="react-select" isSearchable menuPortalTarget={document.body} styles={selectStyles} options={salaryBaseOptions} value={getSelectValue(salaryBaseOptions, settingsForm.salary_base_type)} onChange={(option) => setSettingsForm((current) => ({ ...current, salary_base_type: option?.value || "total_salary" }))} /></label>
                  <label className="staff-loan-form-field"><span>Mulai potong default</span><Select classNamePrefix="react-select" isSearchable menuPortalTarget={document.body} styles={selectStyles} options={startPeriodOptions} value={getSelectValue(startPeriodOptions, settingsForm.default_start_period_rule)} onChange={(option) => setSettingsForm((current) => ({ ...current, default_start_period_rule: option?.value || "next_payroll_period" }))} /></label>
                  <label className="staff-loan-form-field"><span>Butuh approval</span><Select classNamePrefix="react-select" isSearchable menuPortalTarget={document.body} styles={selectStyles} options={yesNoOptions} value={getSelectValue(yesNoOptions, settingsForm.approval_flow_enabled ? "1" : "0")} onChange={(option) => setSettingsForm((current) => ({ ...current, approval_flow_enabled: option?.value === "1" }))} /></label>
                  <label className="staff-loan-form-field"><span>Boleh lebih dari 1 kasbon aktif</span><Select classNamePrefix="react-select" isSearchable menuPortalTarget={document.body} styles={selectStyles} options={yesNoOptions} value={getSelectValue(yesNoOptions, settingsForm.allow_multiple_active_loans ? "1" : "0")} onChange={(option) => setSettingsForm((current) => ({ ...current, allow_multiple_active_loans: option?.value === "1" }))} /></label>
                  <label className="staff-loan-form-field"><span>Boleh jeda cicilan</span><Select classNamePrefix="react-select" isSearchable menuPortalTarget={document.body} styles={selectStyles} options={yesNoOptions} value={getSelectValue(yesNoOptions, settingsForm.allow_pause_installment ? "1" : "0")} onChange={(option) => setSettingsForm((current) => ({ ...current, allow_pause_installment: option?.value === "1" }))} /></label>
                </div>
              </div>
              <div className="card-footer staff-loan-form-actions"><button className="btn btn-primary" disabled={settingsSaving}>{settingsSaving ? "Menyimpan..." : "Simpan Pengaturan"}</button></div>
            </form>
          </PermissionMiddleware>
        )}

        <Modal show={!!selectedLoanId} onHide={() => { setSelectedLoanId(null); setLoanDetail(null); }} size="xl" centered>
          <Modal.Header closeButton><Modal.Title>Detail Kasbon</Modal.Title></Modal.Header>
          <Modal.Body>
            {detailLoading ? <div className="text-center py-4">Loading detail...</div> : loanDetail ? (
              <div className="row g-3">
                <div className="col-lg-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <h3 className="mb-1">{loanDetail.user?.name}</h3>
                      <div className="text-muted mb-3">{loanDetail.loan_number} | {getDepartmentName(loanDetail)}</div>
                      <div className="staff-loan-eligibility-row"><span>Status</span><b><span className={`badge ${statusClass[loanDetail.status]}`}>{statusLabels[loanDetail.status] || loanDetail.status}</span></b></div>
                      <div className="staff-loan-eligibility-row"><span>Nominal</span><b>{formatCurrency(loanDetail.amount)}</b></div>
                      <div className="staff-loan-eligibility-row"><span>Sisa Hutang</span><b>{formatCurrency(loanDetail.outstanding_amount)}</b></div>
                      <div className="staff-loan-eligibility-row"><span>Tenor</span><b>{loanDetail.tenor_months} bulan</b></div>
                      <div className="staff-loan-eligibility-row"><span>Start Period</span><b>{loanDetail.start_period || "-"}</b></div>
                      <div className="mt-3">{renderActionButtons(loanDetail)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-8">
                  <div className="card mb-3">
                    <div className="card-header py-2"><strong>Approval History</strong></div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead><tr><th>Level</th><th>Status</th><th>Approver</th><th>Catatan</th><th>Waktu</th></tr></thead>
                        <tbody>
                          {(loanDetail.approvals || []).length ? loanDetail.approvals.map((approval) => (
                            <tr key={approval.id}>
                              <td>{approval.level}</td>
                              <td>{approval.status}</td>
                              <td>{approval.approver?.name || "-"}</td>
                              <td>{approval.note || "-"}</td>
                              <td>{formatDate(approval.acted_at)}</td>
                            </tr>
                          )) : <tr><td colSpan={5} className="text-muted text-center">Belum ada approval history.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-header py-2"><strong>Jadwal Cicilan</strong></div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead><tr><th>No</th><th>Periode</th><th>Nominal</th><th>Status</th><th>Salary</th><th>Dibayar</th></tr></thead>
                        <tbody>
                          {(loanDetail.repayments || []).length ? loanDetail.repayments.map((repayment) => (
                            <tr key={repayment.id}>
                              <td>{repayment.installment_no}</td>
                              <td>{repayment.period}</td>
                              <td>{formatCurrency(repayment.amount)}</td>
                              <td><span className={`badge ${statusClass[repayment.status] || "bg-secondary-lt text-secondary"}`}>{repayment.status}</span></td>
                              <td>{repayment.salary_id || "-"}</td>
                              <td>{formatDate(repayment.paid_at)}</td>
                            </tr>
                          )) : <tr><td colSpan={6} className="text-muted text-center">Jadwal cicilan dibuat saat pencairan.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </Modal.Body>
        </Modal>

        <Modal show={!!actionDialog} onHide={() => setActionDialog(null)} centered>
          <form onSubmit={handleSubmitAction}>
            <Modal.Header closeButton><Modal.Title>Proses Kasbon</Modal.Title></Modal.Header>
            <Modal.Body>
              <div className="mb-3"><div className="text-muted small">Aksi</div><div className="h3 mb-0 text-capitalize">{actionDialog?.action}</div></div>
              {actionDialog?.action === "disburse" && (
                <div className="row g-2 mb-3">
                  <div className="col-md-6"><label className="form-label">Tanggal Cair</label><input type="date" className="form-control" value={actionDialog.disbursement_date} onChange={(event) => setActionDialog((current) => ({ ...current, disbursement_date: event.target.value }))} /></div>
                  <div className="col-md-6"><label className="form-label">Start Period</label><input className="form-control" placeholder="YYYY-MM" value={actionDialog.start_period} onChange={(event) => setActionDialog((current) => ({ ...current, start_period: event.target.value }))} /></div>
                </div>
              )}
              <label className="form-label">Catatan {actionDialog?.action === "reject" ? "(wajib)" : ""}</label>
              <textarea className="form-control" rows={3} value={actionDialog?.note || ""} onChange={(event) => setActionDialog((current) => ({ ...current, note: event.target.value }))} />
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setActionDialog(null)}>Batal</button>
              <button className="btn btn-primary" disabled={actionSubmitting}>{actionSubmitting ? "Memproses..." : "Simpan"}</button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </LayoutHR>
  );
};

export default StaffLoanPage;
